import { type SearchResultFragment, useLinkConversationToInsuredMutation } from "@qw/graphql";
import { MoreMenu, useToast } from "@qw/ui";
import { type FC, useCallback, useState } from "react";
import { useInboxRail } from "@/inbox/use-inbox-rail";
import { LinkInsuredSearch } from "@/mini-app/components/link-insured-search";
import { UnlinkInsuredButton } from "@/mini-app/components/unlink-insured-button";
import { useInboxQueryStates } from "../providers/inbox-query-states-provider";
import { useConversation } from "../providers/use-conversation";
import { usePage } from "../providers/use-page";

/**
 * This email's insured, linked or unlinked, from beside the star.
 *
 * One slot with two states, the same shape `MiniAppActions` uses — and for the same reason: whether
 * you can link or unlink is one question about one email, so it should not be two controls that
 * have to agree about which conversation they are pointed at.
 *
 * Merged mode only. It is the layout that needs a home for this: the rail keeps the Book until an
 * insured exists, so the mini-app never renders for an unlinked email, and once it does render its
 * own copy of these controls sits in the rail rather than next to the email they act on. In split
 * mode the mini-app is already right there, so this stays out of the way rather than becoming a
 * second button that does the same thing.
 *
 * Own inbox only, too. `isReadOnly` is false on a collaborator-reply thread, but both mutations
 * scope the conversation lookup to the caller's own inbox
 * (`inbox-processing.service.ts:1946-1959`, `1974-1987`) and throw `Conversation not found` for
 * anything else. Replyable is not linkable, so this asks the narrower question.
 */
export const ConversationInsuredButton: FC = () => {
  const { conversationId: selectedConversationId } = useInboxQueryStates();
  const { inboxConversation, isOwnInbox } = useConversation();
  const { isMergedRail } = useInboxRail();
  const { rootPath } = usePage();
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const [linkInsured] = useLinkConversationToInsuredMutation({
    // The rail watches the conversation's `insuredId`, so the refetch that lands it is also what
    // swaps the Book out for the insured.
    refetchQueries: ["InboxConversation"],
  });

  // Apollo keeps the outgoing conversation while the next one loads, so this binds to the selection
  // only once the two agree. Acting on the stale one would link or unlink the email the broker just
  // navigated away from, with nothing on screen to say so.
  const selectedConversation =
    selectedConversationId && inboxConversation?.id === selectedConversationId ? inboxConversation : undefined;
  const conversationId = selectedConversation?.id;
  const handleSelect = useCallback(
    async ({ insuredId }: SearchResultFragment) => {
      if (!conversationId) {
        return;
      }

      const { data } = await linkInsured({ variables: { input: { conversationId, insuredId } } });
      const redirect = data?.linkConversationToInsured?.insuredLinkRedirect;
      if (redirect) {
        toast({ description: redirect.message });
      }
      setOpen(false);
    },
    [conversationId, linkInsured, toast]
  );

  if (!isMergedRail || !isOwnInbox || !selectedConversation) {
    return null;
  }

  if (selectedConversation.insuredId) {
    return <UnlinkInsuredButton conversationId={selectedConversation.id} rootPath={rootPath} />;
  }

  return (
    <MoreMenu
      icon="database_search"
      tooltip="Link insured"
      align="end"
      buttonProps={{ "aria-label": "Link insured", size: "sm" }}
      dropdownMenuProps={{ open, onOpenChange: setOpen }}
      dropdownMenuContentProps={{ className: "w-80 p-1" }}
    >
      <LinkInsuredSearch onSelect={handleSelect} />
    </MoreMenu>
  );
};
